/**
 * Listing-family sell socket — callable surface (Phase 1–4).
 *
 * Owner for buildAttrs / validateForm / hydrate / composeTitle / SpecFields
 * pointers, plus the sell-form bag (`familySpecs`). SpecFields components stay
 * out of this file — the host is the only importer. Registry stays membership.
 */

import {
  buildAcAttributesFromForm,
  composeAcTitleFromAttributes,
  defaultAcSpecFormState,
  getAcSpecFormValidationMessage,
  acFormFromAttributes,
  type AcSpecFormState,
} from "@/lib/listing/acListingSpec";
import {
  isApplianceInverterType,
  isMicrowaveSmartType,
  type ApplianceInverterType,
} from "@/lib/listing/applianceInverterListingSpec";
import {
  buildChairAttributesFromForm,
  chairFormFromAttributes,
  CHAIR_MATERIALS,
  CHAIR_QUANTITY_MODES,
  composeScopedChairLikeTitle,
  defaultChairSpecFormState,
  getChairSpecFormValidationMessage,
  isSofaShape,
  type ChairMaterial,
  type ChairQuantityMode,
  type ChairSpecFormState,
} from "@/lib/listing/chairListingSpec";
import {
  buildDiningTableChairAttributesFromForm,
  composeDiningTableChairTitleFromAttributes,
  defaultDiningTableChairSpecFormState,
  diningTableChairFormFromAttributes,
  getDiningTableChairSpecFormValidationMessage,
  type DiningTableChairSpecFormState,
} from "@/lib/listing/diningTableChairListingSpec";
import {
  buildFanAttributesFromForm,
  composeFanTitleFromAttributes,
  defaultFanSpecFormState,
  getFanSpecFormValidationMessage,
  fanFormFromAttributes,
  isFanMountType,
  isFanPowerSource,
  type FanSpecFormState,
} from "@/lib/listing/fanListingSpec";
import {
  buildFridgeAttributesFromForm,
  composeFridgeTitleFromAttributes,
  defaultFridgeSpecFormState,
  getFridgeSpecFormValidationMessage,
  fridgeFormFromAttributes,
  isFridgeDoorType,
  isFridgeSmartType,
  type FridgeSpecFormState,
} from "@/lib/listing/fridgeListingSpec";
import {
  buildFurnitureSurfaceAttributesFromForm,
  composeFurnitureSurfaceTitleFromAttributes,
  defaultFurnitureSurfaceSpecFormState,
  furnitureSurfaceFormFromAttributes,
  getFurnitureSurfaceSpecFormValidationMessage,
  type FurnitureSurfaceSpecFormState,
} from "@/lib/listing/furnitureSurfaceListingSpec";
import {
  buildFurnitureTableBundleAttributesFromForm,
  composeFurnitureTableBundleTitleFromAttributes,
  defaultFurnitureTableBundleSpecFormState,
  furnitureTableBundleFormFromAttributes,
  getFurnitureTableBundleSpecFormValidationMessage,
  type FurnitureTableBundleSpecFormState,
} from "@/lib/listing/furnitureTableBundleListingSpec";
import {
  buildGasCookerAttributesFromForm,
  composeGasCookerTitleFromAttributes,
  defaultGasCookerSpecFormState,
  getGasCookerSpecFormValidationMessage,
  gasCookerFormFromAttributes,
  isGasCookerMountType,
  isGasCylinderIncludedValue,
  type GasCookerSpecFormState,
} from "@/lib/listing/gasCookerListingSpec";
import {
  buildKitchenCabinetAttributesFromForm,
  composeKitchenCabinetTitleFromAttributes,
  defaultKitchenCabinetSpecFormState,
  getKitchenCabinetSpecFormValidationMessage,
  kitchenCabinetFormFromAttributes,
  isKitchenCabinetMountType,
  type KitchenCabinetSpecFormState,
} from "@/lib/listing/kitchenCabinetListingSpec";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import {
  getListingFamilyById,
  LISTING_FAMILY_CATALOG,
  LISTING_FAMILY_RUNTIME_MODULE,
  resolveListingFamilyBySubcategoryId,
  type ListingFamilyId,
} from "@/lib/listing/listingFamilyRegistry";
import {
  buildMicrowaveAttributesFromForm,
  composeMicrowaveTitleFromAttributes,
  defaultMicrowaveSpecFormState,
  getMicrowaveSpecFormValidationMessage,
  microwaveFormFromAttributes,
  type MicrowaveSpecFormState,
} from "@/lib/listing/microwaveListingSpec";
import {
  buildPlayStationAttributesFromForm,
  composePlayStationTitleFromAttributes,
  defaultPlayStationSpecFormState,
  getPlayStationSpecFormValidationMessage,
  isPlayStationEdition,
  isPlayStationGamesCount,
  isPlayStationGeneration,
  isPlayStationPadCount,
  playStationFormFromAttributes,
  type PlayStationSpecFormState,
} from "@/lib/listing/playStationListingSpec";
import {
  buildSewingMachineAttributesFromForm,
  composeSewingMachineTitleFromAttributes,
  defaultSewingMachineSpecFormState,
  getSewingMachineSpecFormValidationMessage,
  isSewingMachineType,
  sewingMachineFormFromAttributes,
  type SewingMachineSpecFormState,
} from "@/lib/listing/sewingMachineListingSpec";
import {
  buildWashingMachineAttributesFromForm,
  composeWashingMachineTitleFromAttributes,
  defaultWashingMachineSpecFormState,
  getWashingMachineSpecFormValidationMessage,
  isWasherLoaderType,
  washingMachineFormFromAttributes,
  type WashingMachineSpecFormState,
} from "@/lib/listing/washingMachineListingSpec";
import type { ListingSpecAttributes } from "@/lib/listing/specRequirements";

export { LISTING_FAMILY_RUNTIME_MODULE };

export type ListingFamilyHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

export type ListingFamilyComposeTitleOptions = {
  brand?: string | null;
  model?: string | null;
  subcategoryId?: string | null;
};

/** Optional subcategory context for furniture peers that share form state. */
export type ListingFamilySubcategoryContext = {
  subcategoryId?: string | null;
};

/** Path token only. Phase 2 host imports the component. */
export type ListingFamilySpecFieldsPointer = {
  modulePath: string;
  exportName: "default";
};

export type ListingFamilyRuntime = {
  familyId: ListingFamilyId;
  buildAttrs: (
    form: unknown,
    ctx?: ListingFamilySubcategoryContext,
  ) => ListingSpecAttributes;
  validateForm: (
    form: unknown,
    ctx?: ListingFamilySubcategoryContext,
  ) => string | undefined;
  hydrate: (
    attributes: unknown,
    options?: ListingFamilyHydrateOptions,
  ) => unknown;
  composeTitle: (
    attributes: unknown,
    options?: ListingFamilyComposeTitleOptions,
  ) => string;
  specFields: ListingFamilySpecFieldsPointer;
};

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function asFiniteNumberOrEmpty(value: unknown): number | "" {
  if (value === "") return "";
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return "";
}

function asSewingForm(form: unknown): SewingMachineSpecFormState {
  const base = defaultSewingMachineSpecFormState();
  if (!isPlainRecord(form)) return base;
  const type = form.sewingMachineType;
  return {
    sewingMachineType:
      type === "" || isSewingMachineType(type) ? type : base.sewingMachineType,
  };
}

function asPlayStationForm(form: unknown): PlayStationSpecFormState {
  const base = defaultPlayStationSpecFormState();
  if (!isPlainRecord(form)) return base;
  const generation = form.playStationGeneration;
  const edition = form.playStationEdition;
  const padCount = form.padCount;
  const gamesCount = form.gamesCount;
  return {
    playStationGeneration:
      generation === "" || isPlayStationGeneration(generation)
        ? generation
        : base.playStationGeneration,
    playStationEdition:
      edition === "" || isPlayStationEdition(edition)
        ? edition
        : base.playStationEdition,
    padCount:
      padCount === "" || isPlayStationPadCount(padCount)
        ? padCount
        : base.padCount,
    gamesCount:
      gamesCount === "" || isPlayStationGamesCount(gamesCount)
        ? gamesCount
        : base.gamesCount,
  };
}

function asFanForm(form: unknown): FanSpecFormState {
  const base = defaultFanSpecFormState();
  if (!isPlainRecord(form)) return base;
  const mount = form.fanMountType;
  const power = form.fanPowerSource;
  const blade = asFiniteNumberOrEmpty(form.bladeSizeInches);
  return {
    fanMountType:
      mount === "" || isFanMountType(mount) ? mount : base.fanMountType,
    fanPowerSource:
      power === "" || isFanPowerSource(power) ? power : base.fanPowerSource,
    bladeSizeInches: blade === "" ? "" : blade,
  };
}

function asFridgeForm(form: unknown): FridgeSpecFormState {
  const base = defaultFridgeSpecFormState();
  if (!isPlainRecord(form)) return base;
  const door = form.fridgeDoorType;
  const inverter = form.applianceInverterType;
  const smart = form.fridgeSmartType;
  const capacity = asFiniteNumberOrEmpty(form.capacityLiters);
  return {
    fridgeDoorType:
      door === "" || isFridgeDoorType(door) ? door : base.fridgeDoorType,
    capacityLiters: capacity === "" ? "" : capacity,
    applianceInverterType:
      inverter === "" || isApplianceInverterType(inverter)
        ? inverter
        : base.applianceInverterType,
    fridgeSmartType:
      smart === "" || isFridgeSmartType(smart) ? smart : base.fridgeSmartType,
  };
}

function asGasCookerForm(form: unknown): GasCookerSpecFormState {
  const base = defaultGasCookerSpecFormState();
  if (!isPlainRecord(form)) return base;
  const mount = form.gasCookerMountType;
  const cylinder = form.gasCylinderIncluded;
  const burners = asFiniteNumberOrEmpty(form.burnerCount);
  return {
    gasCookerMountType:
      mount === "" || isGasCookerMountType(mount)
        ? mount
        : base.gasCookerMountType,
    gasCylinderIncluded:
      cylinder === "" || isGasCylinderIncludedValue(cylinder)
        ? cylinder
        : base.gasCylinderIncluded,
    burnerCount: burners === "" ? "" : burners,
  };
}

function asMicrowaveForm(form: unknown): MicrowaveSpecFormState {
  const base = defaultMicrowaveSpecFormState();
  if (!isPlainRecord(form)) return base;
  const inverter = form.applianceInverterType;
  const smart = form.microwaveSmartType;
  return {
    applianceInverterType:
      inverter === "" || isApplianceInverterType(inverter)
        ? (inverter as ApplianceInverterType | "")
        : base.applianceInverterType,
    microwaveSmartType:
      smart === "" || isMicrowaveSmartType(smart)
        ? smart
        : base.microwaveSmartType,
  };
}

function asWashingMachineForm(form: unknown): WashingMachineSpecFormState {
  const base = defaultWashingMachineSpecFormState();
  if (!isPlainRecord(form)) return base;
  const load = asFiniteNumberOrEmpty(form.loadCapacityKg);
  const inverter = form.applianceInverterType;
  const loader = form.washerLoaderType;
  return {
    loadCapacityKg: load === "" ? "" : load,
    applianceInverterType:
      inverter === "" || isApplianceInverterType(inverter)
        ? inverter
        : base.applianceInverterType,
    washerLoaderType:
      loader === "" || isWasherLoaderType(loader)
        ? loader
        : base.washerLoaderType,
  };
}

function asAcForm(form: unknown): AcSpecFormState {
  const base = defaultAcSpecFormState();
  if (!isPlainRecord(form)) return base;
  const hp = asFiniteNumberOrEmpty(form.horsePower);
  const inverter = form.applianceInverterType;
  return {
    horsePower: hp === "" ? "" : hp,
    applianceInverterType:
      inverter === "" || isApplianceInverterType(inverter)
        ? inverter
        : base.applianceInverterType,
    acRemoteIncluded: form.acRemoteIncluded === true,
    acInstallationKitIncluded: form.acInstallationKitIncluded === true,
  };
}

function asKitchenCabinetForm(form: unknown): KitchenCabinetSpecFormState {
  const base = defaultKitchenCabinetSpecFormState();
  if (!isPlainRecord(form)) return base;
  const mount = form.kitchenCabinetMountType;
  return {
    kitchenCabinetMountType:
      mount === "" || isKitchenCabinetMountType(mount)
        ? mount
        : base.kitchenCabinetMountType,
  };
}

function isChairMaterialValue(value: unknown): value is ChairMaterial {
  return (
    typeof value === "string" &&
    (CHAIR_MATERIALS as readonly string[]).includes(value)
  );
}

function isChairQuantityModeValue(value: unknown): value is ChairQuantityMode {
  return (
    typeof value === "string" &&
    (CHAIR_QUANTITY_MODES as readonly string[]).includes(value)
  );
}

function asChairForm(form: unknown): ChairSpecFormState {
  const base = defaultChairSpecFormState();
  if (!isPlainRecord(form)) return base;
  const sofaShape = form.sofaShape;
  const mode = form.chairQuantityMode;
  const material = form.chairMaterial;
  const setCount =
    typeof form.chairSetCount === "number" &&
    Number.isFinite(form.chairSetCount)
      ? form.chairSetCount
      : base.chairSetCount;
  const capacities = Array.isArray(form.chairSeatCapacities)
    ? form.chairSeatCapacities.map((c) =>
        typeof c === "number" && Number.isFinite(c) ? c : null,
      )
    : base.chairSeatCapacities;
  return {
    sofaShape:
      sofaShape === "" || isSofaShape(sofaShape) ? sofaShape : base.sofaShape,
    chairQuantityMode:
      mode === "" || isChairQuantityModeValue(mode)
        ? mode
        : base.chairQuantityMode,
    chairSetCount: setCount,
    chairSeatCapacities: capacities,
    chairMaterial:
      material === "" || isChairMaterialValue(material)
        ? material
        : base.chairMaterial,
  };
}

function asDiningTableChairForm(form: unknown): DiningTableChairSpecFormState {
  const base = defaultDiningTableChairSpecFormState();
  if (!isPlainRecord(form)) return base;
  return {
    diningChairCount:
      form.diningChairCount === "" ||
      (typeof form.diningChairCount === "number" &&
        Number.isFinite(form.diningChairCount))
        ? (form.diningChairCount as DiningTableChairSpecFormState["diningChairCount"])
        : base.diningChairCount,
    diningTableTopMaterial:
      typeof form.diningTableTopMaterial === "string"
        ? (form.diningTableTopMaterial as DiningTableChairSpecFormState["diningTableTopMaterial"])
        : base.diningTableTopMaterial,
    chairMaterial:
      form.chairMaterial === "" || isChairMaterialValue(form.chairMaterial)
        ? form.chairMaterial
        : base.chairMaterial,
  };
}

function asFurnitureTableBundleForm(
  form: unknown,
): FurnitureTableBundleSpecFormState {
  const base = defaultFurnitureTableBundleSpecFormState();
  if (!isPlainRecord(form)) return base;
  return {
    chairCount:
      form.chairCount === "" ||
      (typeof form.chairCount === "number" && Number.isFinite(form.chairCount))
        ? (form.chairCount as FurnitureTableBundleSpecFormState["chairCount"])
        : base.chairCount,
    tableTopMaterial:
      typeof form.tableTopMaterial === "string"
        ? (form.tableTopMaterial as FurnitureTableBundleSpecFormState["tableTopMaterial"])
        : base.tableTopMaterial,
    chairMaterial:
      form.chairMaterial === "" || isChairMaterialValue(form.chairMaterial)
        ? form.chairMaterial
        : base.chairMaterial,
    lengthFt: asFiniteNumberOrEmpty(form.lengthFt),
    widthFt: asFiniteNumberOrEmpty(form.widthFt),
    heightFt: asFiniteNumberOrEmpty(form.heightFt),
  };
}

function asFurnitureSurfaceForm(form: unknown): FurnitureSurfaceSpecFormState {
  const base = defaultFurnitureSurfaceSpecFormState();
  if (!isPlainRecord(form)) return base;
  return {
    tableTopMaterial:
      typeof form.tableTopMaterial === "string"
        ? (form.tableTopMaterial as FurnitureSurfaceSpecFormState["tableTopMaterial"])
        : base.tableTopMaterial,
    furnitureMaterial:
      typeof form.furnitureMaterial === "string"
        ? (form.furnitureMaterial as FurnitureSurfaceSpecFormState["furnitureMaterial"])
        : base.furnitureMaterial,
    lengthFt: asFiniteNumberOrEmpty(form.lengthFt),
    widthFt: asFiniteNumberOrEmpty(form.widthFt),
    heightFt: asFiniteNumberOrEmpty(form.heightFt),
  };
}

function resolveCtxSubcategory(
  familyCanonical: string,
  ctx?: ListingFamilySubcategoryContext,
  options?: { subcategoryId?: string | null },
): string {
  return ctx?.subcategoryId ?? options?.subcategoryId ?? familyCanonical;
}

function specFieldsPointer(
  familyId: ListingFamilyId,
): ListingFamilySpecFieldsPointer {
  const family = getListingFamilyById(familyId);
  if (!family) {
    throw new Error(`Missing listing family registration: ${familyId}`);
  }
  return {
    modulePath: family.specFieldsComponent,
    exportName: "default",
  };
}

const SEWING_MACHINE_RUNTIME: ListingFamilyRuntime = {
  familyId: "sewing_machine",
  buildAttrs(form) {
    return buildSewingMachineAttributesFromForm(asSewingForm(form));
  },
  validateForm(form) {
    return getSewingMachineSpecFormValidationMessage(asSewingForm(form));
  },
  hydrate(attributes, options) {
    return sewingMachineFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeSewingMachineTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("sewing_machine"),
};

const PLAY_STATION_RUNTIME: ListingFamilyRuntime = {
  familyId: "play_station",
  buildAttrs(form) {
    return buildPlayStationAttributesFromForm(asPlayStationForm(form));
  },
  validateForm(form) {
    return getPlayStationSpecFormValidationMessage(asPlayStationForm(form));
  },
  hydrate(attributes, options) {
    return playStationFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composePlayStationTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("play_station"),
};

const FAN_RUNTIME: ListingFamilyRuntime = {
  familyId: "fan",
  buildAttrs(form) {
    return buildFanAttributesFromForm(asFanForm(form));
  },
  validateForm(form) {
    return getFanSpecFormValidationMessage(asFanForm(form));
  },
  hydrate(attributes, options) {
    return fanFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeFanTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("fan"),
};

const FRIDGE_RUNTIME: ListingFamilyRuntime = {
  familyId: "fridge",
  buildAttrs(form) {
    return buildFridgeAttributesFromForm(asFridgeForm(form));
  },
  validateForm(form) {
    return getFridgeSpecFormValidationMessage(asFridgeForm(form));
  },
  hydrate(attributes, options) {
    return fridgeFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeFridgeTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("fridge"),
};

const GAS_COOKER_RUNTIME: ListingFamilyRuntime = {
  familyId: "gas_cooker",
  buildAttrs(form) {
    return buildGasCookerAttributesFromForm(asGasCookerForm(form));
  },
  validateForm(form) {
    return getGasCookerSpecFormValidationMessage(asGasCookerForm(form));
  },
  hydrate(attributes, options) {
    return gasCookerFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeGasCookerTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("gas_cooker"),
};

const MICROWAVE_RUNTIME: ListingFamilyRuntime = {
  familyId: "microwave",
  buildAttrs(form) {
    return buildMicrowaveAttributesFromForm(asMicrowaveForm(form));
  },
  validateForm(form) {
    return getMicrowaveSpecFormValidationMessage(asMicrowaveForm(form));
  },
  hydrate(attributes, options) {
    return microwaveFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeMicrowaveTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("microwave"),
};

const WASHING_MACHINE_RUNTIME: ListingFamilyRuntime = {
  familyId: "washing_machine",
  buildAttrs(form) {
    return buildWashingMachineAttributesFromForm(asWashingMachineForm(form));
  },
  validateForm(form) {
    return getWashingMachineSpecFormValidationMessage(
      asWashingMachineForm(form),
    );
  },
  hydrate(attributes, options) {
    return washingMachineFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeWashingMachineTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("washing_machine"),
};

const AC_RUNTIME: ListingFamilyRuntime = {
  familyId: "ac",
  buildAttrs(form) {
    return buildAcAttributesFromForm(asAcForm(form));
  },
  validateForm(form) {
    return getAcSpecFormValidationMessage(asAcForm(form));
  },
  hydrate(attributes, options) {
    return acFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeAcTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("ac"),
};

const KITCHEN_CABINET_RUNTIME: ListingFamilyRuntime = {
  familyId: "kitchen_cabinet",
  buildAttrs(form) {
    return buildKitchenCabinetAttributesFromForm(asKitchenCabinetForm(form));
  },
  validateForm(form) {
    return getKitchenCabinetSpecFormValidationMessage(
      asKitchenCabinetForm(form),
    );
  },
  hydrate(attributes, options) {
    return kitchenCabinetFormFromAttributes(attributes, options);
  },
  composeTitle(attributes, options) {
    return composeKitchenCabinetTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("kitchen_cabinet"),
};

const CHAIR_RUNTIME: ListingFamilyRuntime = {
  familyId: "chair",
  buildAttrs(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("chair", ctx);
    return buildChairAttributesFromForm(subcategoryId, asChairForm(form));
  },
  validateForm(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("chair", ctx);
    return getChairSpecFormValidationMessage(subcategoryId, asChairForm(form));
  },
  hydrate(attributes, options) {
    const subcategoryId = resolveCtxSubcategory("chair", undefined, options);
    return chairFormFromAttributes(subcategoryId, attributes);
  },
  composeTitle(attributes, options) {
    const subcategoryId = resolveCtxSubcategory("chair", undefined, options);
    return composeScopedChairLikeTitle(
      subcategoryId,
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("chair"),
};

const SOFA_RUNTIME: ListingFamilyRuntime = {
  familyId: "sofa",
  buildAttrs(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("sofa-couche", ctx);
    return buildChairAttributesFromForm(subcategoryId, asChairForm(form));
  },
  validateForm(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("sofa-couche", ctx);
    return getChairSpecFormValidationMessage(subcategoryId, asChairForm(form));
  },
  hydrate(attributes, options) {
    const subcategoryId = resolveCtxSubcategory(
      "sofa-couche",
      undefined,
      options,
    );
    return chairFormFromAttributes(subcategoryId, attributes);
  },
  composeTitle(attributes, options) {
    const subcategoryId = resolveCtxSubcategory(
      "sofa-couche",
      undefined,
      options,
    );
    return composeScopedChairLikeTitle(
      subcategoryId,
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("sofa"),
};

const DINING_TABLE_CHAIR_RUNTIME: ListingFamilyRuntime = {
  familyId: "dining_table_chair",
  buildAttrs(form) {
    return buildDiningTableChairAttributesFromForm(
      asDiningTableChairForm(form),
    );
  },
  validateForm(form) {
    return getDiningTableChairSpecFormValidationMessage(
      asDiningTableChairForm(form),
    );
  },
  hydrate(attributes) {
    return diningTableChairFormFromAttributes(attributes);
  },
  composeTitle(attributes, options) {
    return composeDiningTableChairTitleFromAttributes(
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("dining_table_chair"),
};

const TABLE_CHAIR_RUNTIME: ListingFamilyRuntime = {
  familyId: "table_chair",
  buildAttrs(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("table-chair", ctx);
    return buildFurnitureTableBundleAttributesFromForm(
      subcategoryId,
      asFurnitureTableBundleForm(form),
    );
  },
  validateForm(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("table-chair", ctx);
    return getFurnitureTableBundleSpecFormValidationMessage(
      subcategoryId,
      asFurnitureTableBundleForm(form),
    );
  },
  hydrate(attributes) {
    return furnitureTableBundleFormFromAttributes(attributes);
  },
  composeTitle(attributes, options) {
    const subcategoryId = resolveCtxSubcategory(
      "table-chair",
      undefined,
      options,
    );
    return composeFurnitureTableBundleTitleFromAttributes(
      subcategoryId,
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("table_chair"),
};

const OFFICE_TABLE_CHAIRS_RUNTIME: ListingFamilyRuntime = {
  familyId: "office_table_chairs",
  buildAttrs(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("office-table-chairs", ctx);
    return buildFurnitureTableBundleAttributesFromForm(
      subcategoryId,
      asFurnitureTableBundleForm(form),
    );
  },
  validateForm(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("office-table-chairs", ctx);
    return getFurnitureTableBundleSpecFormValidationMessage(
      subcategoryId,
      asFurnitureTableBundleForm(form),
    );
  },
  hydrate(attributes) {
    return furnitureTableBundleFormFromAttributes(attributes);
  },
  composeTitle(attributes, options) {
    const subcategoryId = resolveCtxSubcategory(
      "office-table-chairs",
      undefined,
      options,
    );
    return composeFurnitureTableBundleTitleFromAttributes(
      subcategoryId,
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("office_table_chairs"),
};

const FURNITURE_SURFACE_RUNTIME: ListingFamilyRuntime = {
  familyId: "furniture_surface",
  buildAttrs(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("table", ctx);
    return buildFurnitureSurfaceAttributesFromForm(
      subcategoryId,
      asFurnitureSurfaceForm(form),
    );
  },
  validateForm(form, ctx) {
    const subcategoryId = resolveCtxSubcategory("table", ctx);
    return getFurnitureSurfaceSpecFormValidationMessage(
      subcategoryId,
      asFurnitureSurfaceForm(form),
    );
  },
  hydrate(attributes, options) {
    const subcategoryId = resolveCtxSubcategory("table", undefined, options);
    return furnitureSurfaceFormFromAttributes(subcategoryId, attributes);
  },
  composeTitle(attributes, options) {
    const subcategoryId = resolveCtxSubcategory("table", undefined, options);
    return composeFurnitureSurfaceTitleFromAttributes(
      subcategoryId,
      asListingSpecAttributes(attributes),
      options,
    );
  },
  specFields: specFieldsPointer("furniture_surface"),
};

const RUNTIMES: Readonly<Record<ListingFamilyId, ListingFamilyRuntime>> = {
  sewing_machine: SEWING_MACHINE_RUNTIME,
  play_station: PLAY_STATION_RUNTIME,
  fan: FAN_RUNTIME,
  fridge: FRIDGE_RUNTIME,
  gas_cooker: GAS_COOKER_RUNTIME,
  microwave: MICROWAVE_RUNTIME,
  washing_machine: WASHING_MACHINE_RUNTIME,
  ac: AC_RUNTIME,
  kitchen_cabinet: KITCHEN_CABINET_RUNTIME,
  chair: CHAIR_RUNTIME,
  sofa: SOFA_RUNTIME,
  dining_table_chair: DINING_TABLE_CHAIR_RUNTIME,
  table_chair: TABLE_CHAIR_RUNTIME,
  office_table_chairs: OFFICE_TABLE_CHAIRS_RUNTIME,
  furniture_surface: FURNITURE_SURFACE_RUNTIME,
};

export function getFamilyRuntimeById(
  familyId: ListingFamilyId,
): ListingFamilyRuntime {
  return RUNTIMES[familyId];
}

/** Null when the subcategory is not a registered family. */
export function getFamilyRuntime(
  subcategoryId: string | null | undefined,
): ListingFamilyRuntime | null {
  const family = resolveListingFamilyBySubcategoryId(subcategoryId);
  if (!family) return null;
  return getFamilyRuntimeById(family.familyId);
}

export function listRegisteredFamilyRuntimes(): readonly ListingFamilyRuntime[] {
  return LISTING_FAMILY_CATALOG.map((family) =>
    getFamilyRuntimeById(family.familyId),
  );
}

/** Shared sell-form slot. Keyed by family id, not a named `*Spec` field. */
export type ListingFamilySpecs = Partial<Record<ListingFamilyId, unknown>>;

export function emptyFamilySpecs(): ListingFamilySpecs {
  const specs: ListingFamilySpecs = {};
  for (const family of LISTING_FAMILY_CATALOG) {
    specs[family.familyId] = getFamilyRuntimeById(family.familyId).hydrate(
      undefined,
    );
  }
  return specs;
}

export function readFamilySpec(
  specs: ListingFamilySpecs | undefined,
  familyId: ListingFamilyId,
): unknown {
  return specs?.[familyId] ?? getFamilyRuntimeById(familyId).hydrate(undefined);
}

export function withFamilySpec(
  specs: ListingFamilySpecs | undefined,
  familyId: ListingFamilyId,
  value: unknown,
): ListingFamilySpecs {
  return {
    ...emptyFamilySpecs(),
    ...specs,
    [familyId]: value,
  };
}

/** Refill the bag for the family that owns this subcategory. Others stay empty. */
export function hydrateFamilySpecs(
  subcategoryId: string | null | undefined,
  attributes: unknown,
  options?: ListingFamilyHydrateOptions,
): ListingFamilySpecs {
  const specs = emptyFamilySpecs();
  const runtime = getFamilyRuntime(subcategoryId);
  if (!runtime) return specs;
  specs[runtime.familyId] = runtime.hydrate(attributes, options);
  return specs;
}

export function parseFamilySpecs(
  value: unknown,
): ListingFamilySpecs | undefined {
  if (!isPlainRecord(value)) return undefined;
  const specs: ListingFamilySpecs = {};
  let found = false;
  for (const family of LISTING_FAMILY_CATALOG) {
    if (family.familyId in value) {
      specs[family.familyId] = value[family.familyId];
      found = true;
    }
  }
  return found ? specs : undefined;
}

/** Attrs for a registered family from the bag. Empty object when the slice is absent. */
export function buildRegisteredFamilyAttributes(
  subcategoryId: string | null | undefined,
  specs: ListingFamilySpecs | undefined,
): ListingSpecAttributes {
  const runtime = getFamilyRuntime(subcategoryId);
  if (!runtime || !specs || !(runtime.familyId in specs)) return {};
  return runtime.buildAttrs(readFamilySpec(specs, runtime.familyId), {
    subcategoryId,
  });
}
